import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { API_BASE_URL } from './api-base';

export type MentoringProgramStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
export type MentoringDeliveryType = 'CLASS' | 'ONE_ON_ONE' | 'MIXED';
export type MentoringFormat = 'WHITEBOARD' | 'VIDEO' | 'MATERIAL';
export type MentoringEnrollmentStatus = 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
export type MentoringScheduleStatus = 'SCHEDULED' | 'DONE' | 'CANCELLED';
export type MentoringHistoryAction =
  | 'STATUS_CHANGE'
  | 'SESSION_COMPLETED'
  | 'NOTE_ADDED'
  | 'MEETING_SCHEDULED'
  | 'SESSION_RESCHEDULED'
  | 'WORK_PLAN_UPDATED'
  | 'MENTOR_REGISTERED';
export type MentoringTaskStatus = 'PENDING' | 'DONE';

export interface MentoringProgram {
  id: number;
  tenant_id: number;
  created_by: number;
  name: string;
  description?: string | null;
  photo_path?: string | null;
  cover_linkedin_path?: string | null;
  presentation_file_path?: string | null;
  website?: string | null;
  delivery_type: MentoringDeliveryType;
  format: MentoringFormat;
  status: MentoringProgramStatus;
  created_at: string;
  updated_at?: string | null;
}

export interface MentorOption {
  id: number;
  name: string;
  expertise: string;
  avatar?: string | null;
}

export interface MentoringEnrollment {
  id: number;
  mentoring_program_id: number;
  tenant_id: number;
  mentor_id: number;
  mentorado_user_id?: number | null;
  status: MentoringEnrollmentStatus;
  progress_percent: number;
  session_count?: number | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  mentorado_snapshot: {
    name: string;
    email: string;
    cpf: string;
  };
}

export interface MentoringMentoradoProfile {
  id: number;
  mentoring_enrollment_id: number;
  name: string;
  email: string;
  cpf: string;
  phone?: string | null;
  courses_taken?: string | null;
  profile_description?: string | null;
  education_level?: string | null;
  professional_experience?: string | null;
  extra_experience?: string | null;
  objective?: string | null;
  objective_keywords?: string | null;
  pains_description?: string | null;
  pains_keywords?: string | null;
  work_plan?: string | null;
  created_at: string;
}

export interface MentoringMentoradoBusiness {
  id: number;
  mentoring_enrollment_id: number;
  business_sector?: string | null;
  company_website?: string | null;
  business_description?: string | null;
  job_title?: string | null;
  role_description?: string | null;
  responsibilities?: string | null;
  daily_activities?: string | null;
  created_at: string;
}

export interface MentoringSchedule {
  id: number;
  mentoring_enrollment_id: number;
  tenant_id: number;
  mentorado_name: string;
  mentorado_cpf: string;
  meeting_date: string;
  controltask_task_id?: number | null;
  status: MentoringScheduleStatus;
  created_at: string;
}

export interface MentoringHistoryEntry {
  id: number;
  mentoring_enrollment_id: number;
  tenant_id: number;
  user_id: number;
  action: MentoringHistoryAction;
  description?: string | null;
  created_at: string;
}

export interface MentoringSessionTask {
  id: number;
  mentoring_schedule_id: number;
  title: string;
  notes?: string | null;
  attachment_url?: string | null;
  attachment_name?: string | null;
  sort_order?: number | null;
  status: MentoringTaskStatus;
  created_at: string;
}

export interface MentoringSessionFile {
  id: number;
  mentoring_schedule_id: number;
  file_name: string;
  file_url?: string | null;
  created_at: string;
}

export interface MentoringDashboard {
  activePrograms: number;
  activeEnrollments: number;
  avgProgress: number;
  agendaThisWeek: number;
}

@Injectable({ providedIn: 'root' })
export class ControlMentorService {
  private readonly baseUrl = `${API_BASE_URL}/controlmentor`;

  private programsSubject = new BehaviorSubject<MentoringProgram[]>([]);
  private enrollmentsSubject = new BehaviorSubject<MentoringEnrollment[]>([]);
  private profilesSubject = new BehaviorSubject<MentoringMentoradoProfile[]>([]);
  private businessSubject = new BehaviorSubject<MentoringMentoradoBusiness[]>([]);
  private schedulesSubject = new BehaviorSubject<MentoringSchedule[]>([]);
  private historySubject = new BehaviorSubject<MentoringHistoryEntry[]>([]);
  private taskStreams = new Map<number, BehaviorSubject<MentoringSessionTask[]>>();
  private taskSessionMap = new Map<number, number>();
  private sessionFileStreams = new Map<number, BehaviorSubject<MentoringSessionFile[]>>();

  constructor(private http: HttpClient) {}

  listPrograms(tenantId: number): Observable<MentoringProgram[]> {
    this.refreshPrograms(tenantId);
    return this.programsSubject.asObservable().pipe(
      map((rows) => rows.filter((row) => row.tenant_id === tenantId))
    );
  }

  listEnrollments(tenantId: number): Observable<MentoringEnrollment[]> {
    this.refreshEnrollments(tenantId);
    return this.enrollmentsSubject.asObservable().pipe(
      map((rows) => rows.filter((row) => row.tenant_id === tenantId))
    );
  }

  listSchedules(tenantId: number): Observable<MentoringSchedule[]> {
    this.refreshSchedules(tenantId);
    return this.schedulesSubject.asObservable().pipe(
      map((rows) => rows.filter((row) => row.tenant_id === tenantId))
    );
  }

  listHistory(tenantId: number): Observable<MentoringHistoryEntry[]> {
    this.refreshHistory(tenantId);
    return this.historySubject.asObservable().pipe(
      map((rows) => rows.filter((row) => row.tenant_id === tenantId))
    );
  }

  listMentors(tenantId: number): Observable<MentorOption[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<MentorOption[]>(`${this.baseUrl}/mentors`, { params });
  }

  listTasksForSession(sessionId: number): Observable<MentoringSessionTask[]> {
    if (!this.taskStreams.has(sessionId)) {
      this.taskStreams.set(sessionId, new BehaviorSubject<MentoringSessionTask[]>([]));
    }
    this.refreshSessionTasks(sessionId);
    return this.taskStreams.get(sessionId)!.asObservable();
  }

  getMentoradoProfile(enrollmentId: number): Observable<MentoringMentoradoProfile | null> {
    this.fetchMentoradoProfile(enrollmentId);
    return this.profilesSubject.asObservable().pipe(
      map((rows) => rows.find((row) => row.mentoring_enrollment_id === enrollmentId) || null)
    );
  }

  getMentoradoBusiness(enrollmentId: number): Observable<MentoringMentoradoBusiness | null> {
    this.fetchMentoradoBusiness(enrollmentId);
    return this.businessSubject.asObservable().pipe(
      map((rows) => rows.find((row) => row.mentoring_enrollment_id === enrollmentId) || null)
    );
  }

  getDashboardSummary(tenantId: number): Observable<MentoringDashboard> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<MentoringDashboard>(`${this.baseUrl}/dashboard`, { params });
  }

  createProgram(tenantId: number, createdBy: number, payload: Partial<MentoringProgram>): Observable<MentoringProgram> {
    const body = {
      tenant_id: tenantId,
      created_by: createdBy,
      name: payload.name,
      description: payload.description,
      website: payload.website,
      delivery_type: payload.delivery_type,
      format: payload.format,
      status: payload.status
    };
    return this.http.post<MentoringProgram>(`${this.baseUrl}/programs`, body).pipe(
      tap((program) => this.upsertProgram(program))
    );
  }

  updateProgram(programId: number, payload: Partial<MentoringProgram>) {
    this.http.put<MentoringProgram>(`${this.baseUrl}/programs/${programId}`, payload).subscribe({
      next: (program) => this.upsertProgram(program),
      error: (error) => console.error('Erro ao atualizar programa de mentoria:', error)
    });
  }

  updateProgramStatus(programId: number, status: MentoringProgramStatus) {
    this.http.patch<{ id: number; status: MentoringProgramStatus }>(`${this.baseUrl}/programs/${programId}/status`, { status }).subscribe({
      next: ({ id, status: newStatus }) => {
        const updated = this.programsSubject.value.map((program) =>
          program.id === id ? { ...program, status: newStatus } : program
        );
        this.programsSubject.next(updated);
      },
      error: (error) => console.error('Erro ao atualizar status do programa:', error)
    });
  }

  createEnrollment(tenantId: number, userId: number, payload: {
    mentoring_program_id: number;
    mentor_id: number;
    mentorado_name: string;
    mentorado_email: string;
    mentorado_cpf: string;
    session_count?: number | null;
  }): Observable<MentoringEnrollment> {
    const body = {
      ...payload,
      tenant_id: tenantId,
      user_id: userId
    };
    return this.http.post<MentoringEnrollment>(`${this.baseUrl}/enrollments`, body).pipe(
      tap((enrollment) => {
        this.upsertEnrollment(enrollment);
        this.refreshHistory(tenantId);
      })
    );
  }

  updateEnrollmentStatus(enrollmentId: number, status: MentoringEnrollmentStatus, tenantId: number, userId: number) {
    this.http
      .patch<{ id: number; status: MentoringEnrollmentStatus }>(`${this.baseUrl}/enrollments/${enrollmentId}/status`, {
        status,
        user_id: userId
      })
      .subscribe({
        next: ({ id, status: newStatus }) => {
          const updated = this.enrollmentsSubject.value.map((enrollment) =>
            enrollment.id === id ? { ...enrollment, status: newStatus } : enrollment
          );
          this.enrollmentsSubject.next(updated);
          this.refreshHistory(tenantId);
        },
        error: (error) => console.error('Erro ao atualizar status do mentorado:', error)
      });
  }

  saveMentoradoProfile(
    enrollmentId: number,
    payload: Partial<MentoringMentoradoProfile>,
    options?: { userId?: number; logWorkPlan?: boolean; tenantId?: number }
  ) {
    const body: any = { ...payload };
    if (options?.userId) {
      body.user_id = options.userId;
    }
    if (options?.logWorkPlan) {
      body.log_work_plan = true;
    }
    return this.http.post<MentoringMentoradoProfile>(`${this.baseUrl}/enrollments/${enrollmentId}/profile`, body).pipe(
      tap((profile) => {
        this.setProfile(enrollmentId, profile);
        if (options?.logWorkPlan && options?.tenantId) {
          this.refreshHistory(options.tenantId);
        }
      })
    );
  }

  saveMentoradoBusiness(enrollmentId: number, payload: Partial<MentoringMentoradoBusiness>) {
    return this.http.post<MentoringMentoradoBusiness>(`${this.baseUrl}/enrollments/${enrollmentId}/business`, payload).pipe(
      tap((business) => this.setBusiness(enrollmentId, business))
    );
  }

  scheduleSession(
    tenantId: number,
    payload: {
      mentoring_enrollment_id: number;
      mentorado_name: string;
      mentorado_cpf: string;
      meeting_date: string;
      controltask_task_id?: number | null;
    },
    userId: number
  ): Subscription {
    const body = {
      ...payload,
      tenant_id: tenantId,
      user_id: userId
    };
    return this.http.post<MentoringSchedule>(`${this.baseUrl}/schedules`, body).subscribe({
      next: (schedule) => {
        this.upsertSchedule(schedule);
        this.refreshHistory(tenantId);
      },
      error: (error) => console.error('Erro ao registrar agenda de mentoria:', error)
    });
  }

  updateSchedule(
    scheduleId: number,
    payload: { meeting_date?: string; controltask_task_id?: number | null },
    options?: { userId?: number; tenantId?: number }
  ) {
    const body: any = { ...payload };
    if (options?.userId) {
      body.user_id = options.userId;
    }
    this.http.patch<MentoringSchedule>(`${this.baseUrl}/schedules/${scheduleId}`, body).subscribe({
      next: (schedule) => {
        this.upsertSchedule(schedule);
        if (options?.tenantId) {
          this.refreshHistory(options.tenantId);
        }
      },
      error: (error) => console.error('Erro ao atualizar agenda de mentoria:', error)
    });
  }

  updateSessionStatus(sessionId: number, status: MentoringScheduleStatus, options?: { userId?: number; tenantId?: number }) {
    const body: any = { status };
    if (options?.userId) {
      body.user_id = options.userId;
    }
    this.http
      .patch<{ id: number; status: MentoringScheduleStatus }>(`${this.baseUrl}/schedules/${sessionId}/status`, body)
      .subscribe({
        next: ({ id, status: newStatus }) => {
          const updated = this.schedulesSubject.value.map((schedule) =>
            schedule.id === id ? { ...schedule, status: newStatus } : schedule
          );
          this.schedulesSubject.next(updated);
          if (options?.tenantId) {
            this.refreshHistory(options.tenantId);
          }
        },
        error: (error) => console.error('Erro ao atualizar status da agenda:', error)
      });
  }

  updateSessionTaskStatus(taskId: number, status: MentoringTaskStatus) {
    const sessionId = this.taskSessionMap.get(taskId);
    this.http.patch(`${this.baseUrl}/tasks/${taskId}/status`, { status }).subscribe({
      next: () => {
        if (sessionId) {
          this.refreshSessionTasks(sessionId);
        }
      },
      error: (error) => console.error('Erro ao atualizar status da tarefa da sessão:', error)
    });
  }

  updateSessionTask(taskId: number, payload: { title: string; notes?: string | null }) {
    const sessionId = this.taskSessionMap.get(taskId);
    this.http.patch(`${this.baseUrl}/tasks/${taskId}`, payload).subscribe({
      next: () => {
        if (sessionId) {
          this.refreshSessionTasks(sessionId);
        }
      },
      error: (error) => console.error('Erro ao editar tarefa:', error)
    });
  }

  deleteSessionTask(taskId: number) {
    const sessionId = this.taskSessionMap.get(taskId);
    this.http.delete(`${this.baseUrl}/tasks/${taskId}`).subscribe({
      next: () => {
        if (sessionId) {
          this.refreshSessionTasks(sessionId);
        }
      },
      error: (error) => console.error('Erro ao excluir tarefa:', error)
    });
  }

  reorderSessionTasks(sessionId: number, orderedIds: number[]) {
    if (!orderedIds.length) {
      return;
    }
    this.http
      .patch(`${this.baseUrl}/sessions/${sessionId}/tasks/order`, { ordered_ids: orderedIds })
      .subscribe({
        next: () => this.refreshSessionTasks(sessionId),
        error: (error) => console.error('Erro ao reordenar tarefas da sessão:', error)
      });
  }

  deleteAllSessions(enrollmentId: number, tenantId: number, userId?: number) {
    const body: Record<string, unknown> = {};
    if (userId) {
      body['user_id'] = userId;
    }
    return this.http
      .delete<{ deleted_sessions: number[]; affected: number }>(`${this.baseUrl}/enrollments/${enrollmentId}/sessions`, { body })
      .pipe(
        tap((response) => {
          this.refreshSchedules(tenantId);
          this.refreshHistory(tenantId);
          response?.deleted_sessions?.forEach((sessionId) => {
            this.taskStreams.get(sessionId)?.next([]);
            this.taskStreams.delete(sessionId);
            this.sessionFileStreams.get(sessionId)?.next([]);
            this.sessionFileStreams.delete(sessionId);
          });
        })
      );
  }

  addTaskToSession(sessionId: number, payload: { title: string; notes?: string | null; attachment?: File | null }) {
    const formData = new FormData();
    formData.append('title', payload.title);
    if (payload.notes) {
      formData.append('notes', payload.notes);
    }
    if (payload.attachment) {
      formData.append('attachment', payload.attachment);
    }
    this.http.post(`${this.baseUrl}/sessions/${sessionId}/tasks`, formData).subscribe({
      next: () => this.refreshSessionTasks(sessionId),
      error: (error) => console.error('Erro ao adicionar tarefa da sessão:', error)
    });
  }

  addAgendaNote(enrollmentId: number, tenantId: number, userId: number, description: string) {
    this.http
      .post<MentoringHistoryEntry>(`${this.baseUrl}/history`, {
        mentoring_enrollment_id: enrollmentId,
        user_id: userId,
        action: 'NOTE_ADDED',
        description
      })
      .subscribe({
        next: (entry) => {
          this.historySubject.next([entry, ...this.historySubject.value]);
        },
        error: (error) => console.error('Erro ao registrar observação da mentoria:', error)
      });
  }

  listSessionFiles(sessionId: number): Observable<MentoringSessionFile[]> {
    if (!this.sessionFileStreams.has(sessionId)) {
      this.sessionFileStreams.set(sessionId, new BehaviorSubject<MentoringSessionFile[]>([]));
    }
    this.refreshSessionFiles(sessionId);
    return this.sessionFileStreams.get(sessionId)!.asObservable();
  }

  uploadSessionFile(sessionId: number, file: File): Subscription {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(`${this.baseUrl}/sessions/${sessionId}/files`, formData).subscribe({
      next: () => this.refreshSessionFiles(sessionId),
      error: (error) => console.error('Erro ao anexar arquivo da sessão:', error)
    });
  }

  private refreshPrograms(tenantId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    this.http.get<MentoringProgram[]>(`${this.baseUrl}/programs`, { params }).subscribe({
      next: (rows) => this.programsSubject.next(rows),
      error: (error) => console.error('Erro ao carregar programas de mentoria:', error)
    });
  }

  private refreshEnrollments(tenantId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    this.http.get<MentoringEnrollment[]>(`${this.baseUrl}/enrollments`, { params }).subscribe({
      next: (rows) => this.enrollmentsSubject.next(rows),
      error: (error) => console.error('Erro ao carregar mentorados inscritos:', error)
    });
  }

  private refreshSchedules(tenantId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    this.http.get<MentoringSchedule[]>(`${this.baseUrl}/schedules`, { params }).subscribe({
      next: (rows) => this.schedulesSubject.next(rows),
      error: (error) => console.error('Erro ao carregar agenda de mentoria:', error)
    });
  }

  private refreshHistory(tenantId: number) {
    if (!tenantId) {
      return;
    }
    const params = new HttpParams().set('tenant_id', tenantId);
    this.http.get<MentoringHistoryEntry[]>(`${this.baseUrl}/history`, { params }).subscribe({
      next: (rows) => this.historySubject.next(rows),
      error: (error) => console.error('Erro ao carregar histórico de mentoria:', error)
    });
  }

  private fetchMentoradoProfile(enrollmentId: number) {
    this.http.get<MentoringMentoradoProfile | null>(`${this.baseUrl}/enrollments/${enrollmentId}/profile`).subscribe({
      next: (profile) => this.setProfile(enrollmentId, profile),
      error: (error) => console.error('Erro ao buscar perfil do mentorado:', error)
    });
  }

  private fetchMentoradoBusiness(enrollmentId: number) {
    this.http.get<MentoringMentoradoBusiness | null>(`${this.baseUrl}/enrollments/${enrollmentId}/business`).subscribe({
      next: (business) => this.setBusiness(enrollmentId, business),
      error: (error) => console.error('Erro ao buscar negócio do mentorado:', error)
    });
  }

  private refreshSessionTasks(sessionId: number) {
    this.http.get<MentoringSessionTask[]>(`${this.baseUrl}/sessions/${sessionId}/tasks`).subscribe({
      next: (tasks) => {
        const ordered = tasks.slice().sort((a, b) => {
          const aOrder = a.sort_order ?? 0;
          const bOrder = b.sort_order ?? 0;
          if (aOrder === bOrder) {
            return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          }
          return aOrder - bOrder;
        });
        let stream = this.taskStreams.get(sessionId);
        if (!stream) {
          stream = new BehaviorSubject<MentoringSessionTask[]>(ordered);
          this.taskStreams.set(sessionId, stream);
        } else {
          stream.next(ordered);
        }
        ordered.forEach((task) => this.taskSessionMap.set(task.id, sessionId));
      },
      error: (error) => console.error('Erro ao carregar tarefas da sessão:', error)
    });
  }

  private refreshSessionFiles(sessionId: number) {
    this.http.get<MentoringSessionFile[]>(`${this.baseUrl}/sessions/${sessionId}/files`).subscribe({
      next: (files) => {
        let stream = this.sessionFileStreams.get(sessionId);
        if (!stream) {
          stream = new BehaviorSubject<MentoringSessionFile[]>(files);
          this.sessionFileStreams.set(sessionId, stream);
        } else {
          stream.next(files);
        }
      },
      error: (error) => console.error('Erro ao carregar arquivos da sessão:', error)
    });
  }

  private upsertProgram(program: MentoringProgram) {
    const exists = this.programsSubject.value.some((row) => row.id === program.id);
    if (exists) {
      this.programsSubject.next(this.programsSubject.value.map((row) => (row.id === program.id ? program : row)));
    } else {
      this.programsSubject.next([program, ...this.programsSubject.value]);
    }
  }

  private upsertEnrollment(enrollment: MentoringEnrollment) {
    const exists = this.enrollmentsSubject.value.some((row) => row.id === enrollment.id);
    if (exists) {
      this.enrollmentsSubject.next(this.enrollmentsSubject.value.map((row) => (row.id === enrollment.id ? enrollment : row)));
    } else {
      this.enrollmentsSubject.next([enrollment, ...this.enrollmentsSubject.value]);
    }
  }

  private upsertSchedule(schedule: MentoringSchedule) {
    const exists = this.schedulesSubject.value.some((row) => row.id === schedule.id);
    if (exists) {
      this.schedulesSubject.next(this.schedulesSubject.value.map((row) => (row.id === schedule.id ? schedule : row)));
    } else {
      this.schedulesSubject.next([schedule, ...this.schedulesSubject.value]);
    }
  }

  private setProfile(enrollmentId: number, profile: MentoringMentoradoProfile | null) {
    const filtered = this.profilesSubject.value.filter((row) => row.mentoring_enrollment_id !== enrollmentId);
    if (profile) {
      this.profilesSubject.next([...filtered, profile]);
    } else {
      this.profilesSubject.next(filtered);
    }
  }

  private setBusiness(enrollmentId: number, business: MentoringMentoradoBusiness | null) {
    const filtered = this.businessSubject.value.filter((row) => row.mentoring_enrollment_id !== enrollmentId);
    if (business) {
      this.businessSubject.next([...filtered, business]);
    } else {
      this.businessSubject.next(filtered);
    }
  }
}
